
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Clock, User, Plus, Edit, Trash2 } from "lucide-react";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import ArticleEditor from "@/components/admin/ArticleEditor";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { useAdminRole } from "@/hooks/useAdminRole";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import { useToast } from "@/hooks/use-toast";

interface Article {
  id: string;
  title: string;
  content: string;
  author: string | null;
  reading_time_minutes: number | null;
  created_at: string;
  topic_name: string;
  topic_id: number;
  is_published: boolean;
  updated_at: string;
}

const KinkipediaTopic = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [topicName, setTopicName] = useState("");
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!adminLoading) {
      fetchArticles();
    }
  }, [topicId, isAdmin, adminLoading]);

  const fetchArticles = async () => {
    if (!topicId) return;

    try {
      // Fetch articles - show all to admins, only published to others
      const query = supabase
        .from('kinkipedia_articles')
        .select('*')
        .eq('topic_id', parseInt(topicId))
        .order('created_at', { ascending: true });

      // Non-admins only see published articles
      if (!isAdmin) {
        query.eq('is_published', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data && data.length > 0) {
        setArticles(data);
        setTopicName(data[0].topic_name);
      } else {
        // Fetch topic name even if no articles
        const { data: topicData } = await supabase
          .from('kinkipedia_topics')
          .select('name')
          .eq('id', parseInt(topicId))
          .single();
        
        if (topicData) {
          setTopicName(topicData.name);
        }
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (articleId: string) => {
    try {
      const { error } = await supabase
        .from('kinkipedia_articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Article deleted successfully",
      });
      
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive",
      });
    }
  };

  const handleArticleSaved = () => {
    setEditingArticle(null);
    setShowCreateDialog(false);
    fetchArticles();
  };

  const handleArticleClick = (articleId: string) => {
    navigate(`/kinkipedia/article/${articleId}`);
  };

  const handleBackClick = () => {
    navigate('/kinkipedia');
  };

  if (loading || adminLoading) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <AlphaLoader size="large" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-carbon flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
      <AdPanel />
      <RightSideMenu />
      
      <div className="flex-1 p-4 pb-20">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="text-ice-white hover:bg-carbon/50 hover:text-neon-red border border-ice-white/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Topics
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-ice-white mb-2">{topicName}</h1>
          <div className="flex items-center justify-between">
            <p className="text-ice-white/60">
              {articles.length} article{articles.length !== 1 ? 's' : ''} available
            </p>
            {isAdmin && (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-toxic-green hover:bg-toxic-green/80 text-carbon">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Article</DialogTitle>
                    <DialogDescription>
                      Create a new article for {topicName}
                    </DialogDescription>
                  </DialogHeader>
                  <ArticleEditor 
                    onSave={handleArticleSaved}
                    article={{
                      id: '',
                      title: '',
                      content: '',
                      topic_id: parseInt(topicId || '1'),
                      topic_name: topicName,
                      author: null,
                      reading_time_minutes: null,
                      is_published: false,
                      created_at: '',
                      updated_at: ''
                    }}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Articles */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {articles.map((article) => (
            <Card 
              key={article.id} 
              className="bg-carbon/80 border-ice-white/20 text-ice-white cursor-pointer hover:scale-105 transition-all duration-300 hover:border-neon-red/50 hover:shadow-neon-red/20 relative group"
            >
              {/* Admin Controls */}
              {isAdmin && (
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Dialog open={editingArticle?.id === article.id} onOpenChange={(open) => !open && setEditingArticle(null)}>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingArticle(article);
                        }}
                        className="h-8 w-8 p-0 bg-carbon/50 hover:bg-neon-red/20"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                      <DialogHeader>
                        <DialogTitle>Edit Article</DialogTitle>
                        <DialogDescription>
                          Modify the article content and settings
                        </DialogDescription>
                      </DialogHeader>
                      {editingArticle && (
                        <ArticleEditor 
                          article={editingArticle} 
                          onSave={handleArticleSaved} 
                        />
                      )}
                    </DialogContent>
                  </Dialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 w-8 p-0 bg-carbon/50 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Article</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{article.title}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteArticle(article.id)}
                          className="bg-destructive hover:bg-destructive/80"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              {/* Status Badge for Admins */}
              {isAdmin && !article.is_published && (
                <Badge 
                  variant="outline" 
                  className="absolute top-2 left-2 bg-orange-500/20 border-orange-500/30 text-orange-400 text-xs"
                >
                  Draft
                </Badge>
              )}

              <div onClick={() => handleArticleClick(article.id)}>
                <CardHeader>
                  <CardTitle className="text-lg leading-tight text-ice-white">{article.title}</CardTitle>
                  <div className="flex items-center gap-4 text-sm text-ice-white/60">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{article.reading_time_minutes || 5} min read</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      <span>{article.author || 'Unknown'}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-ice-white/70 text-sm line-clamp-3 mb-3">
                    {article.content.substring(0, 150)}...
                  </p>
                  <Badge variant="outline" className="bg-carbon/30 text-ice-white border-ice-white/30 hover:bg-neon-red/20 hover:border-neon-red/50">
                    Read Article
                  </Badge>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-12">
            <h3 className="text-xl font-bold text-ice-white/70 mb-2">No articles available</h3>
            <p className="text-ice-white/50">Articles for this topic are coming soon</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KinkipediaTopic;
